import { useMemo, useState } from 'react';

import {
	BUDGET,
	POSITIONS,
	SQUAD_QUOTA,
	SQUAD_SIZE,
	allPlayers,
	formatCost,
	rejectionReason,
	totalCost,
} from './draftRules.js';
import './PlayerList.css';

/*
 * Price bands, in tenths of a million.
 *
 * Chosen against where players actually sit rather than round numbers:
 * two thirds of the list is packed into £4.0-5.9m, so these three cut the
 * bunched end into thirds. Wider bands looked tidier and did nothing —
 * £10m kept 99% of the list, since only two players are priced above it.
 */
const PRICE_BANDS = [45, 55, 65];

const bandLabel = (tenths) => `≤${(tenths / 10).toFixed(1).replace(/\.0$/, '')}m`;

const SORTS = {
	points_desc: { label: 'Points ↓', compare: (a, b) => b.points - a.points },
	cost_desc: { label: 'Price ↓', compare: (a, b) => b.cost - a.cost },
	cost_asc: { label: 'Price ↑', compare: (a, b) => a.cost - b.cost },
	tsb_desc: { label: 'Owned ↓', compare: (a, b) => b.tsb - a.tsb },
};

/*
 * The player list, alongside the pitch.
 *
 * Clicking a player picks them — they go to the first free slot of their
 * own position. There is no slot to select first: the squad has fixed
 * quotas, so the destination is never ambiguous, and making someone point
 * at a hole before filling it was a step that only ever had one answer.
 */
export default function PlayerList({ players, squad, onPick }) {
	const [query, setQuery] = useState('');
	const [club, setClub] = useState('');
	/* Points first: the list opens on the players worth having, which is a
	 * better starting point than the most expensive ones. */
	const [sort, setSort] = useState('points_desc');
	const [position, setPosition] = useState('');
	const [maxCost, setMaxCost] = useState(null);

	const clubs = useMemo(
		() => [...new Set(players.map((player) => player.team_name))].sort(),
		[players],
	);

	const rows = useMemo(() => {
		const needle = query.trim().toLowerCase();
		return players
			.filter((player) => !position || player.position === position)
			.filter((player) => !needle || player.name.toLowerCase().includes(needle))
			.filter((player) => !club || player.team_name === club)
			.filter((player) => !maxCost || player.cost <= maxCost)
			.map((player) => ({ player, reason: rejectionReason(squad, player) }))
			.sort((a, b) => SORTS[sort].compare(a.player, b.player));
	}, [players, position, query, club, sort, maxCost, squad]);

	const pickable = rows.filter((row) => !row.reason).length;

	/*
	 * The squad, restated inside the list.
	 *
	 * Stacked under the pitch on a phone, the list fills the screen on its
	 * own: at the moment of picking, the pitch is 50px of grass at the top
	 * edge and the budget bar is scrolled off entirely, so every pick is
	 * made without sight of what it costs or what the squad still needs.
	 * These two are that information, put where the picking happens. Hidden
	 * on desktop, where the header bar is already in view.
	 */
	const squadState = useMemo(() => {
		const remaining = BUDGET - totalCost(squad);
		return {
			remaining,
			filled: allPlayers(squad).length,
			counts: Object.fromEntries(
				POSITIONS.map((code) => [code, squad[code].filter(Boolean).length]),
			),
		};
	}, [squad]);

	return (
		<section className="plist" aria-label="Players">
			<p className="plist__status">
				<span
					className={`plist__budget${
						squadState.remaining < 0 ? ' plist__budget--over' : ''
					}`}
				>
					{squadState.remaining < 0
						? `${formatCost(-squadState.remaining)} over`
						: `${formatCost(squadState.remaining)} left`}
				</span>
				<span className="plist__progress">
					{squadState.filled}/{SQUAD_SIZE} picked
				</span>
			</p>

			<div className="plist__filters">
				<input
					className="plist__input plist__input--search"
					type="search"
					value={query}
					placeholder="Search"
					aria-label="Search players"
					onChange={(event) => setQuery(event.target.value)}
				/>
				<select
					className="plist__input"
					value={club}
					aria-label="Filter by club"
					onChange={(event) => setClub(event.target.value)}
				>
					<option value="">All clubs</option>
					{clubs.map((name) => (
						<option key={name} value={name}>
							{name}
						</option>
					))}
				</select>
				<select
					className="plist__input"
					value={sort}
					aria-label="Sort players"
					onChange={(event) => setSort(event.target.value)}
				>
					{Object.entries(SORTS).map(([key, { label }]) => (
						<option key={key} value={key}>
							{label}
						</option>
					))}
				</select>
			</div>

			{/* Position and price are the two cuts made constantly while
			    filling a squad, so both are one tap rather than a select.
			    Pressing the active chip clears it, which is why neither row
			    carries an extra "All".

			    Each chip also carries how many of that position are picked.
			    It is the "what do I still need" question, answered on the
			    control you would use to act on the answer, and it costs no
			    room of its own. */}
			<div className="plist__chips" role="group" aria-label="Filter by position">
				{POSITIONS.map((code) => {
					const active = position === code;
					const held = squadState.counts[code];
					return (
						<button
							key={code}
							type="button"
							className="plist__chip"
							data-position={code}
							data-full={held === SQUAD_QUOTA[code] || undefined}
							aria-pressed={active}
							aria-label={`${code}, ${held} of ${SQUAD_QUOTA[code]} picked`}
							onClick={() => setPosition(active ? '' : code)}
						>
							{code}{' '}
							<span className="plist__chip-count">
								{held}/{SQUAD_QUOTA[code]}
							</span>
						</button>
					);
				})}
			</div>

			<div className="plist__chips" role="group" aria-label="Filter by price">
				{PRICE_BANDS.map((band) => {
					const active = maxCost === band;
					return (
						<button
							key={band}
							type="button"
							className="plist__chip"
							aria-pressed={active}
							onClick={() => setMaxCost(active ? null : band)}
						>
							{bandLabel(band)}
						</button>
					);
				})}
				<span className="plist__count">{pickable} available</span>
			</div>

			{rows.length === 0 ? (
				<p className="plist__empty">Nothing matches.</p>
			) : (
				<ul className="plist__list">
					{rows.map(({ player, reason }) => (
						<li key={player.element}>
							<button
								type="button"
								className="plist__row"
								disabled={Boolean(reason)}
								onClick={() => onPick(player)}
								title={reason || undefined}
							>
								{/* Only worth showing when the list is mixed — filtered
								    to one position it would repeat on every row. */}
								{!position && (
									<span className="plist__pos" data-position={player.position}>
										{player.position}
									</span>
								)}
								<span className="plist__player">
									<span className="plist__name">{player.name}</span>
									<span className="plist__meta">
										{player.team_name} · {player.points} pts · {player.tsb}%
									</span>
								</span>
								{reason ? (
									<span className="plist__reason">{reason}</span>
								) : (
									<span className="plist__cost">{formatCost(player.cost)}</span>
								)}
							</button>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}
